import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  FaBars,
  FaChalkboardTeacher,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
  FaSearch,
  FaShieldAlt,
  FaTimes,
  FaUser,
} from "react-icons/fa";

import { getSettings } from "../../services/settingsServices";
import { useUser } from "../../pages/Home/UserContext";

import { useDashboardLayout } from "../contexts/DashboardLayoutContext";

import {
  getUserRoleId,
  getVisibleDashboardSections,
  isDashboardMenuActive,
  unwrapSettings,
} from "../config/dashboardMenuConfig";
import { useMessage } from "../../hooks/MessageContext";

const TOPBAR_HEIGHT = "82px";
const STORAGE_KEY = "mac_dashboard_open_sections";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function getDefaultOpenSections(roleId) {
  if (roleId === 1) return ["utilisateur", "enseignant", "administrateur"];
  if (roleId === 3) return ["utilisateur", "enseignant"];
  return ["utilisateur"];
}

const formatUnreadBadge = (count) => {
  const value = Number(count || 0);
  if (value <= 0) return null;
  return value > 99 ? "99+" : String(value);
};

const isMessagingMenuItem = (item = {}) => {
  const raw = `${item.title || ""} ${item.path || ""}`
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  return (
    raw.includes("messagerie") ||
    raw.includes("message") ||
    raw.includes("messages")
  );
};

const getSectionBadgeCount = (section) => {
  return (section?.items || []).reduce((sum, item) => {
    const n = Number(item?.badgeValue || item?.badge || 0);
    return Number.isFinite(n) ? sum + n : sum;
  }, 0);
};

const ROLE_THEMES = {
  utilisateur: {
    label: "Utilisateur",
    shortLabel: "U",
    icon: <FaUser />,
    dot: "bg-sky-500",
    text: "text-sky-700 dark:text-sky-200",
    chip:
      "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-200",
    border: "border-sky-100 dark:border-sky-500/20",
    profileGradient:
      "bg-gradient-to-br from-sky-500 via-blue-600 to-indigo-700",
    activeGradient:
      "bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-700",
    shadow:
      "shadow-[0_18px_35px_-22px_rgba(37,99,235,0.95)]",
    headerIdle:
      "border-sky-100 bg-sky-50/95 text-sky-950 hover:bg-sky-100 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-100 dark:hover:bg-sky-500/15",
    itemIdle:
      "text-slate-600 hover:bg-sky-50 hover:text-sky-900 dark:text-slate-300 dark:hover:bg-sky-500/10 dark:hover:text-sky-100",
    iconIdle:
      "bg-sky-50 text-sky-700 ring-sky-100 group-hover:bg-sky-100 dark:bg-sky-500/10 dark:text-sky-200 dark:ring-sky-500/20 dark:group-hover:bg-sky-500/20",
  },

  enseignant: {
    label: "Enseignant",
    shortLabel: "E",
    icon: <FaChalkboardTeacher />,
    dot: "bg-emerald-500",
    text: "text-emerald-700 dark:text-emerald-200",
    chip:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-200",
    border: "border-emerald-100 dark:border-emerald-500/20",
    profileGradient:
      "bg-gradient-to-br from-emerald-500 via-green-600 to-teal-700",
    activeGradient:
      "bg-gradient-to-r from-emerald-500 via-green-600 to-teal-700",
    shadow:
      "shadow-[0_18px_35px_-22px_rgba(16,185,129,0.95)]",
    headerIdle:
      "border-emerald-100 bg-emerald-50/95 text-emerald-950 hover:bg-emerald-100 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-100 dark:hover:bg-emerald-500/15",
    itemIdle:
      "text-slate-600 hover:bg-emerald-50 hover:text-emerald-900 dark:text-slate-300 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-100",
    iconIdle:
      "bg-emerald-50 text-emerald-700 ring-emerald-100 group-hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-500/20 dark:group-hover:bg-emerald-500/20",
  },

  administrateur: {
    label: "Administrateur",
    shortLabel: "A",
    icon: <FaShieldAlt />,
    dot: "bg-red-500",
    text: "text-red-700 dark:text-red-200",
    chip:
      "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-200",
    border: "border-red-100 dark:border-red-500/20",
    profileGradient:
      "bg-gradient-to-br from-red-500 via-rose-600 to-red-800",
    activeGradient:
      "bg-gradient-to-r from-red-500 via-rose-600 to-red-800",
    shadow:
      "shadow-[0_18px_35px_-22px_rgba(225,29,72,0.95)]",
    headerIdle:
      "border-red-100 bg-red-50/95 text-red-950 hover:bg-red-100 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-100 dark:hover:bg-red-500/15",
    itemIdle:
      "text-slate-600 hover:bg-red-50 hover:text-red-900 dark:text-slate-300 dark:hover:bg-red-500/10 dark:hover:text-red-100",
    iconIdle:
      "bg-red-50 text-red-700 ring-red-100 group-hover:bg-red-100 dark:bg-red-500/10 dark:text-red-200 dark:ring-red-500/20 dark:group-hover:bg-red-500/20",
  },
};

function getRoleKeyFromRoleId(roleId) {
  if (roleId === 1) return "administrateur";
  if (roleId === 3) return "enseignant";
  return "utilisateur";
}

function getRoleTheme(roleId) {
  return ROLE_THEMES[getRoleKeyFromRoleId(roleId)] || ROLE_THEMES.utilisateur;
}

function getSectionTheme(section = {}) {
  const raw = normalizeText(`${section.key || ""} ${section.label || ""}`);

  if (
    raw.includes("admin") ||
    raw.includes("administrateur") ||
    raw.includes("administration")
  ) {
    return ROLE_THEMES.administrateur;
  }

  if (
    raw.includes("enseignant") ||
    raw.includes("coach") ||
    raw.includes("teacher")
  ) {
    return ROLE_THEMES.enseignant;
  }

  return ROLE_THEMES.utilisateur;
}

function ProfileSummary({ roleId, totalPages, isCollapsed, mobile = false }) {
  const theme = getRoleTheme(roleId);

  if (isCollapsed && !mobile) {
    return (
      <div className="mb-4 flex justify-center px-3">
        <div
          title={`Profil actif : ${theme.label}`}
          className={cn(
            "flex h-14 w-14 items-center justify-center rounded-[1.35rem] text-xl text-white ring-1 ring-white/20",
            theme.profileGradient,
            theme.shadow,
          )}
        >
          {theme.icon}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "mx-4 mb-3 overflow-hidden rounded-[1.65rem] p-4 text-white",
        theme.profileGradient,
        theme.shadow,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/70">
            Profil actif
          </p>

          <p className="mt-1 truncate font-montserrat text-base font-bold">
            {theme.label}
          </p>
        </div>

        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-lg ring-1 ring-white/20">
          {theme.icon}
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-white/12 px-3 py-2 ring-1 ring-white/15">
        <p className="text-xs font-medium text-white/85">
          {totalPages} page{totalPages > 1 ? "s" : ""} accessible
          {totalPages > 1 ? "s" : ""}
        </p>
      </div>
    </div>
  );
}

function ActiveLocationCard({
  activeContext,
  fallbackRoleId,
  isCollapsed,
  mobile = false,
}) {
  const fallbackTheme = getRoleTheme(fallbackRoleId);
  const theme = activeContext?.theme || fallbackTheme;

  if (isCollapsed && !mobile) {
    return null;
  }

  return (
    <div
      className={cn(
        "mx-4 mb-4 rounded-[1.65rem] border bg-white p-3 shadow-sm dark:bg-slate-950",
        theme.border,
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-lg text-white",
            theme.activeGradient,
          )}
        >
          {theme.icon}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
            Vous êtes ici
          </p>

          <p className="mt-1 truncate font-montserrat text-sm font-bold text-slate-900 dark:text-white">
            {activeContext?.item?.title || "Dashboard"}
          </p>

          <div className="mt-1 flex min-w-0 items-center gap-2">
            <span className={cn("h-2 w-2 shrink-0 rounded-full", theme.dot)} />
            <span className={cn("truncate text-xs font-bold", theme.text)}>
              {activeContext?.section?.label || theme.label}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MenuSearch({ value, onChange, mobile = false }) {
  return (
    <div className={cn(mobile ? "mx-3 mb-4" : "mx-4 mb-4")}>
      <label className="sr-only" htmlFor={mobile ? "dashboard-search-mobile" : "dashboard-search"}>
        Rechercher une page
      </label>

      <div className="relative">
        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
          <FaSearch />
        </span>

        <input
          id={mobile ? "dashboard-search-mobile" : "dashboard-search"}
          type="search"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Rechercher une page..."
          className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-medium text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#013E8E] focus:bg-white focus:ring-4 focus:ring-blue-500/10 dark:border-slate-800 dark:bg-slate-950 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-400/10"
        />
      </div>
    </div>
  );
}

function SectionSeparator({ label, theme, isCollapsed, mobile = false }) {
  if (isCollapsed && !mobile) {
    return (
      <div className="mx-3 my-4 flex items-center justify-center">
        <div
          title={label}
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-2xl text-sm ring-1",
            theme.chip,
            theme.border,
          )}
        >
          {theme.icon}
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 pb-2 pt-3">
      <div className="flex items-center gap-3">
        <div className={cn("h-px flex-1", theme.dot)} />
        <p className={cn("shrink-0 text-[10px] font-bold uppercase tracking-[0.24em]", theme.text)}>
          {label}
        </p>
        <div className={cn("h-px flex-1", theme.dot)} />
      </div>
    </div>
  );
}

function MenuItem({
  item,
  active,
  theme,
  isCollapsed,
  mobile = false,
  onNavigate,
}) {
  const hasBadge =
    item.badge !== null &&
    item.badge !== undefined &&
    String(item.badge).length > 0;

  return (
    <NavLink
      to={item.path}
      onClick={onNavigate}
      title={item.title}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group relative mx-2 flex items-center overflow-hidden rounded-[1.35rem] border px-2.5 py-2.5 text-sm transition-all duration-200",
        isCollapsed && !mobile ? "justify-center" : "gap-3",
        active
          ? cn(
              "border-transparent text-white ring-1 ring-white/10 hover:text-white",
              theme.activeGradient,
              theme.shadow,
            )
          : cn("border-transparent", theme.itemIdle),
      )}
    >
      {active && (
        <span className="absolute inset-y-3 left-0 w-1 rounded-r-full bg-white/70" />
      )}

      <span
        className={cn(
          "relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-base ring-1 transition-all",
          active
            ? "bg-white/16 text-white ring-white/20 group-hover:bg-white/20"
            : theme.iconIdle,
        )}
      >
        {item.icon}

        {hasBadge && isCollapsed && !mobile && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-[10px] font-bold leading-none text-white shadow ring-2 ring-white dark:ring-slate-900">
            {item.badge}
          </span>
        )}
      </span>

      {(!isCollapsed || mobile) && (
        <span
          className={cn(
            "min-w-0 flex-1 truncate",
            active ? "font-bold" : "font-medium",
          )}
        >
          {item.title}
        </span>
      )}

      {active && (!isCollapsed || mobile) && (
        <span className="hidden shrink-0 rounded-full bg-white/16 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white ring-1 ring-white/20 xl:inline-flex">
          Ici
        </span>
      )}

      {hasBadge && (!isCollapsed || mobile) && (
        <span
          className={cn(
            "flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full px-2 text-[10px] font-bold leading-none shadow-sm",
            active ? "bg-white text-slate-900" : "bg-red-600 text-white",
          )}
          title={`${item.badge} message(s) non lu(s)`}
        >
          {item.badge}
        </span>
      )}
    </NavLink>
  );
}

function SectionBlock({
  section,
  pathname,
  isOpen,
  isCollapsed,
  mobile = false,
  searchActive = false,
  onToggle,
  onNavigate,
}) {
  const theme = getSectionTheme(section);

  const hasActiveItem = section.items.some((item) =>
    isDashboardMenuActive(pathname, item),
  );

  const sectionBadgeCount = getSectionBadgeCount(section);
  const sectionBadge = formatUnreadBadge(sectionBadgeCount);

  const visibleCount = section.visibleCount ?? section.items.length;
  const totalCount = section.totalCount ?? section.items.length;
  const forcedOpen = searchActive || isOpen;

  if (isCollapsed && !mobile) {
    return (
      <div className="mb-2">
        <SectionSeparator
          label={section.label}
          theme={theme}
          isCollapsed={isCollapsed}
          mobile={mobile}
        />

        <div className="space-y-1">
          {section.items.map((item) => {
            const active = isDashboardMenuActive(pathname, item);

            return (
              <MenuItem
                key={item.path}
                item={item}
                active={active}
                theme={theme}
                isCollapsed={isCollapsed}
                mobile={mobile}
                onNavigate={onNavigate}
              />
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "relative mx-2 flex w-[calc(100%-1rem)] items-center gap-3 overflow-hidden rounded-[1.5rem] border px-3 py-3 text-left transition-all duration-200",
          hasActiveItem
            ? cn(
                "border-transparent text-white",
                theme.activeGradient,
                theme.shadow,
              )
            : theme.headerIdle,
        )}
      >
        {hasActiveItem && (
          <span className="absolute inset-y-3 left-0 w-1.5 rounded-r-full bg-white/70" />
        )}

        <span
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-base ring-1",
            hasActiveItem
              ? "bg-white/16 text-white ring-white/20"
              : cn("bg-white/80 ring-white/80 dark:bg-white/5 dark:ring-white/10", theme.text),
          )}
        >
          {theme.icon}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate font-montserrat text-sm font-bold">
            {section.label}
          </span>

          <span
            className={cn(
              "mt-0.5 block text-xs",
              hasActiveItem
                ? "text-white/82"
                : "text-slate-500 dark:text-slate-400",
            )}
          >
            {searchActive
              ? `${visibleCount} / ${totalCount} page${totalCount > 1 ? "s" : ""}`
              : `${section.items.length} page${section.items.length > 1 ? "s" : ""}`}
          </span>
        </span>

        {sectionBadge && (
          <span
            className={cn(
              "flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full px-2 text-[10px] font-bold leading-none shadow-sm",
              hasActiveItem ? "bg-white text-slate-900" : "bg-red-600 text-white",
            )}
            title={`${sectionBadgeCount} message(s) non lu(s)`}
          >
            {sectionBadge}
          </span>
        )}

        <span className="shrink-0 text-sm">
          {forcedOpen ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </button>

      {forcedOpen && (
        <div className="mt-2 space-y-1">
          {section.items.map((item) => {
            const active = isDashboardMenuActive(pathname, item);

            return (
              <MenuItem
                key={item.path}
                item={item}
                active={active}
                theme={theme}
                isCollapsed={isCollapsed}
                mobile={mobile}
                onNavigate={onNavigate}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function DashboardAsideMenu() {
  const { user } = useUser();
  const { isCollapsed, setIsCollapsed } = useDashboardLayout();

  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const roleId = getUserRoleId(user);

  const [openSections, setOpenSections] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = JSON.parse(raw);

      if (Array.isArray(parsed)) return parsed;
    } catch {}

    return getDefaultOpenSections(roleId);
  });

  const { unreadMessageCount } = useMessage();

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const data = await getSettings();

        if (!mounted) return;

        setSettings(unwrapSettings(data));
      } catch (error) {
        console.error(
          "Erreur lors du chargement des settings du dashboard",
          error,
        );

        if (!mounted) return;

        setSettings({});
      } finally {
        if (mounted) setSettingsLoaded(true);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const baseSections = useMemo(() => {
    return getVisibleDashboardSections(user, settings);
  }, [user, settings]);

  const sections = useMemo(() => {
    const unreadBadge = formatUnreadBadge(unreadMessageCount);

    return baseSections.map((section) => ({
      ...section,
      items: (section.items || []).map((item) => {
        if (!isMessagingMenuItem(item)) return item;

        return {
          ...item,
          badge: unreadBadge,
          badgeValue: Number(unreadMessageCount || 0),
        };
      }),
    }));
  }, [baseSections, unreadMessageCount]);

  const totalPages = useMemo(() => {
    return sections.reduce((sum, section) => {
      return sum + Number(section?.items?.length || 0);
    }, 0);
  }, [sections]);

  const normalizedSearch = useMemo(() => {
    return normalizeText(searchValue);
  }, [searchValue]);

  const filteredSections = useMemo(() => {
    if (!normalizedSearch) {
      return sections.map((section) => ({
        ...section,
        visibleCount: section.items.length,
        totalCount: section.items.length,
      }));
    }

    return sections
      .map((section) => {
        const items = section.items || [];

        const filteredItems = items.filter((item) => {
          const raw = normalizeText(`${item.title || ""} ${item.path || ""}`);
          return raw.includes(normalizedSearch);
        });

        return {
          ...section,
          items: filteredItems,
          visibleCount: filteredItems.length,
          totalCount: items.length,
        };
      })
      .filter((section) => section.items.length > 0);
  }, [sections, normalizedSearch]);

  const activeContext = useMemo(() => {
    for (const section of sections) {
      const activeItem = (section.items || []).find((item) =>
        isDashboardMenuActive(location.pathname, item),
      );

      if (activeItem) {
        return {
          section,
          item: activeItem,
          theme: getSectionTheme(section),
        };
      }
    }

    return null;
  }, [sections, location.pathname]);

  useEffect(() => {
    const allowedKeys = baseSections.map((section) => section.key);

    setOpenSections((prev) => {
      const cleaned = prev.filter((key) => allowedKeys.includes(key));

      if (cleaned.length > 0) return cleaned;

      return getDefaultOpenSections(roleId).filter((key) =>
        allowedKeys.includes(key),
      );
    });
  }, [roleId, baseSections]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(openSections));
    } catch {}
  }, [openSections]);

  const shellWidth = useMemo(() => {
    return isCollapsed ? "w-24" : "w-72";
  }, [isCollapsed]);

  const closeMobile = () => setMobileOpen(false);

  const toggleSection = (sectionKey) => {
    setOpenSections((prev) => {
      if (prev.includes(sectionKey)) {
        return prev.filter((key) => key !== sectionKey);
      }

      return [...prev, sectionKey];
    });
  };

  const renderEmptySearch = () => (
    <div className="mx-3 mt-4 rounded-[1.5rem] border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
      <p className="font-bold text-slate-800 dark:text-white">
        Aucun résultat
      </p>
      <p className="mt-1 text-xs leading-relaxed">
        Essaie avec un autre mot-clé : adhérent, règlement, groupe, ceinture,
        message...
      </p>
    </div>
  );

  const renderSections = ({ mobile = false } = {}) => {
    const searchActive = normalizedSearch.length > 0;

    if (!settingsLoaded && sections.length === 0) {
      return (
        <div className="mx-3 mt-4 rounded-[1.5rem] border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
          Chargement du menu...
        </div>
      );
    }

    if (sections.length === 0) {
      return (
        <div className="mx-3 mt-4 rounded-[1.5rem] border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
          Aucune page disponible pour ce profil.
        </div>
      );
    }

    if (searchActive && filteredSections.length === 0) {
      return renderEmptySearch();
    }

    return filteredSections.map((section) => (
      <SectionBlock
        key={section.key}
        section={section}
        pathname={location.pathname}
        isOpen={openSections.includes(section.key)}
        isCollapsed={isCollapsed}
        mobile={mobile}
        searchActive={searchActive}
        onToggle={() => toggleSection(section.key)}
        onNavigate={mobile ? closeMobile : undefined}
      />
    ));
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="fixed left-4 z-[70] flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white/95 text-slate-800 shadow-lg backdrop-blur transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900/95 dark:text-white dark:hover:bg-slate-800 lg:hidden"
        style={{ top: "98px" }}
        aria-label="Ouvrir le menu du dashboard"
      >
        <FaBars />
      </button>

      <aside
        className={cn(
          "fixed left-0 z-40 hidden flex-col border-r border-slate-200/80 bg-white/95 backdrop-blur-xl transition-all duration-300 dark:border-slate-800/80 dark:bg-slate-900/95 lg:flex",
          shellWidth,
        )}
        style={{
          top: TOPBAR_HEIGHT,
          height: `calc(100dvh - ${TOPBAR_HEIGHT})`,
        }}
      >
        <div className="flex items-center justify-end gap-3 px-4 pb-3 pt-4">
          <button
            type="button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="ml-auto flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            aria-label={isCollapsed ? "Déplier le menu" : "Replier le menu"}
          >
            {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
        </div>

        <ProfileSummary
          roleId={roleId}
          totalPages={totalPages}
          isCollapsed={isCollapsed}
        />

        {!isCollapsed && (
          <>
            <ActiveLocationCard
              activeContext={activeContext}
              fallbackRoleId={roleId}
              isCollapsed={isCollapsed}
            />

            <MenuSearch value={searchValue} onChange={setSearchValue} />
          </>
        )}

        <div className="flex-1 overflow-y-auto px-1 pb-6">
          {renderSections()}
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-[80] lg:hidden">
          <div
            className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm"
            onClick={closeMobile}
          />

          <aside
            className="absolute left-0 w-[88vw] max-w-[370px] overflow-hidden border-r border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
            style={{
              top: TOPBAR_HEIGHT,
              height: `calc(100dvh - ${TOPBAR_HEIGHT})`,
            }}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-4 dark:border-slate-800">
              <div className="min-w-0">
                <p className="truncate font-montserrat text-sm font-bold text-slate-900 dark:text-white">
                  MonAppliClub
                </p>
                <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                  Navigation du dashboard
                </p>
              </div>

              <button
                type="button"
                onClick={closeMobile}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                aria-label="Fermer le menu"
              >
                <FaTimes />
              </button>
            </div>

            <div className="h-[calc(100%-73px)] overflow-y-auto px-2 pb-6 pt-4">
              <ProfileSummary
                roleId={roleId}
                totalPages={totalPages}
                isCollapsed={false}
                mobile
              />

              <ActiveLocationCard
                activeContext={activeContext}
                fallbackRoleId={roleId}
                isCollapsed={false}
                mobile
              />

              <MenuSearch
                value={searchValue}
                onChange={setSearchValue}
                mobile
              />

              {renderSections({ mobile: true })}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}